import { clsx, type ClassValue } from "clsx";

import { X } from "lucide-react";
import { createContext, useContext, useRef } from "react";
import { Tabs as RadixTabs } from "radix-ui";

import { twMerge } from "@/design-system/twMerge";

type TabsSize = "regular" | "small";

const TabsSizeContext = createContext<TabsSize>("regular");

export function tabListClassnames(overrides?: ClassValue, scrollable = false) {
  return twMerge(
    clsx(scrollable && "flex overflow-x-auto *:shrink-0", overrides),
  );
}
export function tabTriggerClassnames(
  overrides?: ClassValue,
  size: TabsSize = "regular",
) {
  return twMerge(
    clsx(
      'px-4 py-3 cursor-pointer text-textSubtle data-[state="active"]:text-textActionSelected transition-all rounded-xs border-b-2 border-transparent data-[state="active"]:border-textActionSelected disabled:opacity-50 disabled:cursor-not-allowed',
      size === "small" ? "text-heading-xs" : "text-heading-md",
      overrides,
    ),
  );
}
export function tabContentClassnames(overrides?: ClassValue) {
  return twMerge(clsx(overrides));
}

interface ListProps extends RadixTabs.TabsListProps {
  /** Keeps the triggers on one row and scrolls horizontally when they overflow. */
  scrollable?: boolean;
}

function List({ className, scrollable, ...props }: ListProps) {
  return (
    <RadixTabs.List
      className={tabListClassnames(className, scrollable)}
      {...props}
    />
  );
}

interface TriggerProps extends RadixTabs.TabsTriggerProps {
  /**
   * Renders a close button beside the trigger and calls this when it's clicked
   * or when Delete is pressed on the focused trigger.
   */
  onClose?: () => void;
  /** Accessible name of the close button. */
  closeLabel?: string;
}

// The close button is a sibling of the trigger, not a child: the trigger is a
// <button>, and nesting another button inside it is invalid HTML. It stays out
// of the Tab order so the tablist keeps one Tab stop and holds only tabs;
// keyboard users close the focused tab with Delete, as in the WAI-ARIA Tabs
// pattern.
function Trigger({
  className,
  onClose,
  closeLabel = "Close tab",
  onKeyDown,
  ...props
}: TriggerProps) {
  const size = useContext(TabsSizeContext);
  const wrapperRef = useRef<HTMLDivElement>(null);
  if (!onClose) {
    return (
      <RadixTabs.Trigger
        className={tabTriggerClassnames(className, size)}
        onKeyDown={onKeyDown}
        {...props}
      />
    );
  }

  const close = () => {
    const tabList = wrapperRef.current?.closest('[role="tablist"]');
    onClose();
    // Closing usually unmounts the focused element, dropping focus to <body>.
    // Focus the selected trigger rather than a neighbour: Radix selects a
    // trigger as soon as it gains focus, which would switch tabs.
    requestAnimationFrame(() => {
      if (document.activeElement && document.activeElement !== document.body) {
        return;
      }
      tabList
        ?.querySelector<HTMLElement>('[role="tab"][data-state="active"]')
        ?.focus();
    });
  };

  return (
    <div ref={wrapperRef} className="inline-flex items-stretch">
      <RadixTabs.Trigger
        className={tabTriggerClassnames(
          clsx("peer rounded-r-none pr-1", className),
          size,
        )}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === "Delete" && !event.defaultPrevented) {
            close();
          }
        }}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={closeLabel}
        disabled={props.disabled}
        onClick={close}
        className="text-textSubtle hover:text-textDefault peer-data-[state=active]:text-textActionSelected peer-data-[state=active]:border-textActionSelected outline-focus-ring flex cursor-pointer items-center rounded-r-xs border-b-2 border-transparent pr-3 pl-1 transition-all focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}

function Content({ className, ...props }: RadixTabs.TabsContentProps) {
  return (
    <RadixTabs.Content className={tabContentClassnames(className)} {...props} />
  );
}

interface RootProps extends RadixTabs.TabsProps {
  size?: TabsSize;
}

function Root({ size = "regular", ...props }: RootProps) {
  return (
    <TabsSizeContext.Provider value={size}>
      <RadixTabs.Root {...props} />
    </TabsSizeContext.Provider>
  );
}

export const Tabs = {
  Root,
  List,
  Trigger,
  Content,
};
